<script setup>

import { computed, ref, watch } from 'vue'
import { useAuth0 } from '@auth0/auth0-vue'
import md5 from 'md5'
import { useUserStore } from '@/stores/user.js'
import ClickToEditTextInputComponent from '@/components/common/ClickToEditTextInputComponent.vue'

const { user, getAccessTokenSilently, } = useAuth0()
const userStore = useUserStore()

const username = ref(userStore.name)
const email = ref(userStore.email)


const gravatarURI = computed(() => {
  const trimmedEmail = user?.value?.email?.trim()?.toLowerCase() ?? null
  if (!trimmedEmail) return

  const hash = md5(trimmedEmail).toString()
  return `https://gravatar.com/avatar/${hash}?size=256`
})

watch(userStore, (store) => {
  username.value = store.name
  email.value = store.email
})

async function onUpdateNickName() {
  if (username.value === userStore.name) {
    return
  }

  const isConfirmed = confirm(`Are you sure you want to change your name to ${username.value}`)
  if (!isConfirmed) {
    username.value = userStore.name
    return
  }

  const token = await getAccessTokenSilently()
  await fetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/users/profile`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      nickname: username.value
    })
  })

  await userStore.retrieveProfile()
}

</script>

<template>
  <div class="max-w-screen-2xl w-full self-center flex flex-col lg:flex-row gap-4 mt-24 pb-8 px-4">
    <div class="lg:w-2/3 h-full flex flex-col gap-4">
      <div class="border-white/10 border bg-white/5 rounded-lg w-full h-fit p-4 pt-2">
        <div class="text-lg text-stone-200">You</div>
        <div class="flex flex-row gap-4 mt-4">
          <div class="flex flex-col gap-2 items-center">
            <img class="w-24 h-24 rounded-full border border-white/10" :src=gravatarURI>
            <span class="text-stone-300 text-xs">Gravatar</span>
          </div>
          <div class="flex flex-col justify-center ">
            <div class="flex flex-row items-center">
              <span class="text-sm text-stone-200">Username:</span>
              <ClickToEditTextInputComponent v-model="username" :value="userStore.name" @blur="onUpdateNickName"
                                             :style="'text-stone-500 ml-2 p-0'" />
            </div>
            <div class="flex flex-row items-center">
              <span class="text-sm text-stone-200">Email:</span>
              <ClickToEditTextInputComponent v-model="email" :value="userStore.email" :readonly="true"
                                             :style="'text-stone-200 ml-2 p-0 cursor-default'" />
            </div>
          </div>
        </div>

      </div>

      <div class="border-white/10 border bg-white/5 rounded-lg w-full h-fit p-4 pt-2">
        <div class="text-lg text-stone-200">Team</div>
        <div class="text-sm text-stone-300 mt-4">The Teams functionality is currently in development. It will allow you
          to invite colleagues to your team, create private rooms only accessible by members in your team and more
          useful functionality.
        </div>

      </div>
    </div>
    <div class="lg:w-1/3 h-full flex flex-col gap-4">
      <div class="border-white/10 border bg-white/5 rounded-lg w-full h-fit p-4 pt-2">
        <div class="text-lg text-stone-200">Room History</div>
        <div class="text-sm text-stone-300 mt-4">Coming Soon</div>
      </div>
      <div class="border-white/10 border bg-white/5 rounded-lg w-full h-fit p-4 pt-2">
        <div class="text-lg text-stone-200">Billing</div>
        <div class="text-sm text-stone-300 whitespace-pre-wrap mt-4">Aesti is currently free to use. In the future we
          will offer billing plans to allow for further development of Aesti.
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped>

</style>
